import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from 'react-router-dom';
import config from './config';

interface ExamCourse {
  id: number;
  name: string;
}

interface ExamDetail {
  id: number;
  name: string;
  description: string | null;
  timeLimitMinutes: number;
  isActive: boolean;
  totalQuestionCount: number;
  enabledQuestionCount: number;
  createdAt: string;
  updatedAt: string;
  courses: ExamCourse[];
}

interface UserExamAttempt {
  id: number;
  userId: number;
  examId: number;
  examName?: string | null;
  attemptNumber: number;
  status: string;
  startedAt: string;
  completedAt?: string | null;
  percentage: number;
  isPassed: boolean;
}

export const ExamCenter: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [exam, setExam] = useState<ExamDetail | null>(null);
  const [attempts, setAttempts] = useState<UserExamAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const examId = Number(id);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const headers: HeadersInit = {};
        if (isAuthenticated) {
          try {
            const token = await getAccessTokenSilently();
            headers.Authorization = `Bearer ${token}`;
          } catch (tokenError) {
            console.warn('Unable to get access token for exam center', tokenError);
          }
        }

        const examResponse = await fetch(`${config.apiBaseUrl}/api/exams/${examId}`, { headers });
        if (!examResponse.ok) {
          throw new Error('Failed to load exam details');
        }

        const examData: ExamDetail = await examResponse.json();
        setExam(examData);
      } catch (fetchError) {
        console.error(fetchError);
        setError('Failed to load exam details');
      } finally {
        setLoading(false);
      }
    };

    if (examId) {
      fetchData();
    } else {
      setError('Invalid exam id');
      setLoading(false);
    }
  }, [examId, isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    const fetchAttempts = async () => {
      if (!isAuthenticated || !examId) {
        setAttempts([]);
        return;
      }

      try {
        setHistoryLoading(true);
        const token = await getAccessTokenSilently();
        const response = await fetch(`${config.apiBaseUrl}/api/examattempts/me?examId=${examId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to load attempt history');
        }

        const data: UserExamAttempt[] = await response.json();
        setAttempts(data);
      } catch (historyError) {
        console.error(historyError);
        setAttempts([]);
      } finally {
        setHistoryLoading(false);
      }
    };

    fetchAttempts();
  }, [examId, isAuthenticated, getAccessTokenSilently]);

  const latestAttempt = useMemo(() => attempts[0] ?? null, [attempts]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !exam) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="mx-auto max-w-3xl rounded-2xl bg-white p-6 shadow-lg">
          <div className="text-lg font-semibold text-red-700">{error || 'Exam not found'}</div>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="rounded-3xl bg-white p-6 shadow-xl sm:p-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 text-sm font-medium text-indigo-600 hover:text-indigo-700"
          >
            Back to Exams
          </button>

          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex-1">
              <p className="text-sm font-semibold uppercase tracking-wide text-indigo-600">Exam Details</p>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">{exam.name}</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                {exam.description || 'Timed multiple-choice exam with saved attempt history and review support.'}
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {exam.courses.map((course) => (
                  <span
                    key={course.id}
                    className="rounded-full bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-700"
                  >
                    {course.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="w-full rounded-2xl border border-indigo-100 bg-indigo-50 p-5 lg:max-w-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-white p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Questions</p>
                  <p className="mt-2 text-2xl font-bold text-slate-900">{exam.enabledQuestionCount || exam.totalQuestionCount}</p>
                </div>
                <div className="rounded-xl bg-white p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Duration</p>
                  <p className="mt-2 text-2xl font-bold text-slate-900">{exam.timeLimitMinutes} min</p>
                </div>
              </div>

              {latestAttempt && (
                <div className="mt-4 rounded-xl border border-indigo-200 bg-white p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Latest Attempt</p>
                  <p className="mt-2 text-lg font-bold text-slate-900">{latestAttempt.percentage.toFixed(1)}%</p>
                  <p className={`mt-1 text-sm font-medium ${latestAttempt.isPassed ? 'text-green-600' : 'text-red-600'}`}>
                    {latestAttempt.isPassed ? 'Passed' : 'Needs improvement'}
                  </p>
                </div>
              )}

              <button
                onClick={() => navigate(`/exam/${exam.id}`)}
                className="mt-5 w-full rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
              >
                Start Exam
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-white p-6 shadow-xl sm:p-8">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Previous Attempts</h2>
              <p className="mt-1 text-sm text-slate-500">Review saved attempts, including correctness and selected answers.</p>
            </div>
          </div>

          {!isAuthenticated ? (
            <div className="mt-6 rounded-2xl border border-indigo-200 bg-indigo-50 p-5">
              <p className="text-sm font-semibold text-indigo-900">Login required to view exam history</p>
              <p className="mt-1 text-sm text-indigo-700">You can still start the exam, but previous attempts and saved results are only available after login.</p>
              <button
                onClick={() => loginWithRedirect({
                  appState: { returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}` },
                })}
                className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
              >
                Login to View History
              </button>
            </div>
          ) : historyLoading ? (
            <div className="mt-6 text-sm text-slate-500">Loading attempt history...</div>
          ) : attempts.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-600">
              No previous attempts found for this exam.
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              {attempts.map((attempt) => (
                <div
                  key={attempt.id}
                  className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-semibold text-slate-900">Attempt #{attempt.attemptNumber}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${attempt.isPassed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {attempt.isPassed ? 'Passed' : 'Failed'}
                      </span>
                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
                        {attempt.status}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-slate-600">
                      {new Date(attempt.completedAt || attempt.startedAt).toLocaleString()} | Score: {attempt.percentage.toFixed(1)}%
                    </p>
                  </div>

                  <button
                    onClick={() => navigate(`/exam-attempt/${attempt.id}`)}
                    className="rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
                  >
                    Review Attempt
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
